import styled, { css, RuleSet } from 'styled-components'

import { media } from '@/theme/common'
import { Colors } from '@/theme/defaults'

import {
  TypographyBreakLinesType,
  TypographySizeKeys,
  TypographyTextAlignKeys,
  TypographyTextTransformKeys,
  TypographyWeightKeys,
  TypographyWhiteSpaceKeys,
  TypographyWordBreakKeys,
} from './typography.types'

export const Typography = styled.p<{
  $size: TypographySizeKeys
  $color?: keyof Colors
  $weight?: TypographyWeightKeys
  $breakLines?: TypographyBreakLinesType
  $textTransform?: TypographyTextTransformKeys
  $textAlign?: TypographyTextAlignKeys
  $whiteSpace?: TypographyWhiteSpaceKeys
  $wordBreak?: TypographyWordBreakKeys
}>(
  ({
    theme,
    $size,
    $weight,
    $color,
    $breakLines,
    $textTransform,
    $textAlign,
    $whiteSpace,
    $wordBreak,
  }) => {
    const baseStyles = css`
      letter-spacing: 0;
      margin: 0;
      padding: 0;
      color: ${$color ? theme.colors[$color] : 'inherit'};
    `

    // Note: the media.down('md')/media.down('sm') blocks below step each size down the scale
    // for tablet/mobile - each tier reuses the next-smaller variant's exact existing values
    // (tablet = one tier down, mobile = two tiers down) rather than introducing new rem
    // literals, so there's no new visual size anywhere that isn't already used somewhere else
    // in this same map. heading-tiny/text-small are the floor (smallest heading/text tier) and
    // never shrink further; heading-small/text-medium land on that floor already at the tablet
    // tier, so they only need the one override block. media.down('sm') must come after
    // media.down('md') in each block - both match below 600px, and source order (not just
    // specificity) is what makes the narrower/later block win for that overlapping range.
    const sizeStyles: Record<TypographySizeKeys, RuleSet> = {
      'heading-huge': css`
        font-size: 2.4rem;
        line-height: 3.2rem;
        font-weight: 600;

        ${media.down('md')} {
          font-size: 2rem;
          line-height: 2.8rem;
        }

        ${media.down('sm')} {
          font-size: 1.8rem;
          line-height: 2.4rem;
        }
      `,
      'heading-large': css`
        font-size: 2rem;
        line-height: 2.8rem;
        font-weight: 600;

        ${media.down('md')} {
          font-size: 1.8rem;
          line-height: 2.4rem;
        }

        ${media.down('sm')} {
          font-size: 1.6rem;
          line-height: 2.4rem;
        }
      `,
      'heading-medium': css`
        font-size: 1.8rem;
        line-height: 2.4rem;
        font-weight: 600;

        ${media.down('md')} {
          font-size: 1.6rem;
          line-height: 2.4rem;
        }

        ${media.down('sm')} {
          font-size: 1.4rem;
          line-height: 2rem;
        }
      `,
      'heading-small': css`
        font-size: 1.6rem;
        line-height: 2.4rem;
        font-weight: 600;

        ${media.down('md')} {
          font-size: 1.4rem;
          line-height: 2rem;
        }
      `,
      'heading-tiny': css`
        font-size: 1.4rem;
        line-height: 2rem;
        font-weight: 600;
      `,
      'text-huge': css`
        font-size: 1.8rem;
        line-height: 2.4rem;
        font-weight: 500;

        ${media.down('md')} {
          font-size: 1.6rem;
          line-height: 2.4rem;
        }

        ${media.down('sm')} {
          font-size: 1.4rem;
          line-height: 2rem;
        }
      `,
      'text-large': css`
        font-size: 1.6rem;
        line-height: 2.4rem;
        font-weight: 400;

        ${media.down('md')} {
          font-size: 1.4rem;
          line-height: 2rem;
        }

        ${media.down('sm')} {
          font-size: 1.2rem;
          line-height: 1.6rem;
        }
      `,
      'text-medium': css`
        font-size: 1.4rem;
        line-height: 2rem;
        font-weight: 400;

        ${media.down('md')} {
          font-size: 1.2rem;
          line-height: 1.6rem;
        }
      `,
      'text-small': css`
        font-size: 1.2rem;
        line-height: 1.6rem;
        font-weight: 400;
      `,
      unset: css``,
    }

    const weightStyles: Record<TypographyWeightKeys, RuleSet> = {
      bold: css`
        font-weight: 700;
      `,
      light: css`
        font-weight: 300;
      `,
      medium: css`
        font-weight: 500;
      `,
      regular: css`
        font-weight: 400;
      `,
      semibold: css`
        font-weight: 600;
      `,
    }

    const textTransformStyles =
      $textTransform &&
      css`
        text-transform: ${$textTransform};
      `

    const textAlignStyles =
      $textAlign &&
      css`
        text-align: ${$textAlign};
      `

    const whiteSpaceStyles =
      $whiteSpace &&
      css`
        white-space: ${$whiteSpace};
      `

    const wordBreakStyles =
      $wordBreak &&
      css`
        word-break: ${$wordBreak};
      `

    const breakLinesStyles =
      $breakLines === 1 || $breakLines === true
        ? css`
            display: block;
            overflow: hidden;
            white-space: ${$whiteSpace ?? 'nowrap'};
            text-overflow: ellipsis;
            word-break: keep-all;
          `
        : typeof $breakLines === 'number' && $breakLines > 1
          ? css`
              display: -webkit-box;
              overflow: hidden;
              text-overflow: ellipsis;
              word-break: break-word;
              -webkit-line-clamp: ${$breakLines};
              -webkit-box-orient: vertical;
            `
          : undefined

    return css`
      ${baseStyles}
      ${sizeStyles[$size]}
    ${$weight ? weightStyles[$weight] : ''}
    ${textTransformStyles}
    ${textAlignStyles}
    ${whiteSpaceStyles}
    ${wordBreakStyles}
    ${breakLinesStyles}
    `
  },
)
